// NEW_FILE_JUSTIFIED: New control panel page for superadmin organisation management — no prior OrganisationsManagementPage exists
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/control/ are two levels deeper than src/pages/, requiring three levels up to reach convex/_generated/
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Building2, ShieldOff, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { BlockConfirmDialog } from '@/components/control/BlockConfirmDialog';
import type { ISuperadminOrg, IBlockOrgTarget } from '@/@types/superadmin';

function OrgTableSkeleton() {
  return (
    <>
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center px-6 py-3 border-b border-table-row-border gap-4">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-32 ml-4" />
          <Skeleton className="h-4 w-28 ml-4" />
          <Skeleton className="h-4 w-16 ml-4" />
          <Skeleton className="h-4 w-16 ml-4" />
          <Skeleton className="h-4 w-16 ml-4" />
          <Skeleton className="h-4 w-20 ml-4" />
          <Skeleton className="h-4 w-24 ml-4" />
          <Skeleton className="h-4 w-16 ml-auto" />
        </div>
      ))}
    </>
  );
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const statusStyles = {
  active:
    'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-400/20',
  blocked:
    'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-500/10 dark:text-red-400 dark:ring-red-400/20',
};

export function OrganisationsManagementPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [blockTarget, setBlockTarget] = useState<IBlockOrgTarget | null>(null);
  const [isBlockLoading, setIsBlockLoading] = useState(false);

  const rawOrgs = useQuery(api['superadmin/queries'].listAllOrganisations, {});
  const blockOrg = useMutation(api['superadmin/mutations'].blockOrganisation);
  const unblockOrg = useMutation(api['superadmin/mutations'].unblockOrganisation);

  const isLoading = rawOrgs === undefined;

  const filtered = useMemo(() => {
    const list: ISuperadminOrg[] = (rawOrgs ?? []) as ISuperadminOrg[]; // AS_JUSTIFIED: Convex useQuery returns a structurally compatible type; explicit cast aligns with ISuperadminOrg
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      (org: ISuperadminOrg) =>
        org.name.toLowerCase().includes(q) ||
        org.industry.toLowerCase().includes(q) ||
        org.ownerName?.toLowerCase().includes(q) ||
        org.ownerEmail?.toLowerCase().includes(q)
    );
  }, [rawOrgs, searchQuery]);

  const handleBlockClick = (e: React.MouseEvent, org: ISuperadminOrg) => {
    e.stopPropagation();
    setBlockTarget({ id: org._id, name: org.name, isBlocked: !!org.blockedAt });
  };

  const handleBlockConfirm = async () => {
    if (!blockTarget) return;
    setIsBlockLoading(true);
    try {
      if (blockTarget.isBlocked) {
        await unblockOrg({ orgId: blockTarget.id });
        toast.success(`${blockTarget.name} has been unblocked`);
      } else {
        await blockOrg({ orgId: blockTarget.id });
        toast.success(`${blockTarget.name} has been blocked`);
      }
      setBlockTarget(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setIsBlockLoading(false);
    }
  };

  return (
    <TooltipProvider>
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Organisations</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {isLoading ? 'Loading...' : `${rawOrgs?.length ?? 0} total`}
            </p>
          </div>
        </div>

        <div className="bg-table-container border border-table-container-border rounded-2xl overflow-hidden shadow">
          <div className="flex items-center gap-3 px-6 py-3 border-b border-table-row-border">
            <label htmlFor="org-search" className="sr-only">
              Search organisations
            </label>
            <Input
              id="org-search"
              placeholder="Search by name, industry or owner..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="max-w-xs"
            />
          </div>

          <div className="flex items-center px-6 py-2 border-b border-table-row-border bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            <span className="w-44 shrink-0">Name</span>
            <span className="w-36 shrink-0">Owner</span>
            <span className="w-32 shrink-0">Industry</span>
            <span className="w-20 shrink-0">Members</span>
            <span className="w-24 shrink-0">Companies</span>
            <span className="w-20 shrink-0">Policies</span>
            <span className="w-24 shrink-0">Status</span>
            <span className="flex-1">Created</span>
            <span className="w-16 shrink-0 text-right">Actions</span>
          </div>

          <div className="overflow-auto">
            {isLoading && <OrgTableSkeleton />}

            {!isLoading &&
              filtered.map((org: ISuperadminOrg) => {
                const isBlocked = !!org.blockedAt;
                return (
                  <div
                    key={org._id}
                    className={cn(
                      'flex items-center px-6 py-3 border-b border-table-row-border hover:bg-muted/50 cursor-pointer',
                      isBlocked && 'opacity-60'
                    )}
                    onClick={() => navigate(`/control/organisations/${org._id}`)}
                  >
                    <span className="w-44 shrink-0 text-sm font-medium text-foreground truncate pr-3">
                      {org.name}
                    </span>
                    <span className="w-36 shrink-0 text-sm text-muted-foreground truncate pr-3">
                      {org.ownerName ?? org.ownerEmail ?? '—'}
                    </span>
                    <span className="w-32 shrink-0 text-sm text-muted-foreground truncate pr-3">
                      {org.industry}
                    </span>
                    <span className="w-20 shrink-0 text-sm text-muted-foreground">
                      {org.memberCount}
                    </span>
                    <span className="w-24 shrink-0 text-sm text-muted-foreground">
                      {org.companyCount}
                    </span>
                    <span className="w-20 shrink-0 text-sm text-muted-foreground">
                      {org.policyCount}
                    </span>
                    <span className="w-24 shrink-0">
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset',
                          isBlocked ? statusStyles.blocked : statusStyles.active
                        )}
                      >
                        {isBlocked ? 'Blocked' : 'Active'}
                      </span>
                    </span>
                    <span className="flex-1 text-sm text-muted-foreground">
                      {formatDate(org.createdAt)}
                    </span>
                    <div className="w-16 shrink-0 flex justify-end">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                              'h-7 w-7',
                              isBlocked
                                ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-500/10'
                                : 'text-destructive hover:text-destructive hover:bg-destructive/10'
                            )}
                            onClick={e => handleBlockClick(e, org)}
                            aria-label={isBlocked ? `Unblock ${org.name}` : `Block ${org.name}`}
                          >
                            {isBlocked ? (
                              <ShieldCheck className="w-4 h-4" />
                            ) : (
                              <ShieldOff className="w-4 h-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{isBlocked ? 'Unblock' : 'Block'}</TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                );
              })}

            {!isLoading && filtered.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-1">No organisations found</h3>
                <p className="text-sm text-muted-foreground">
                  {searchQuery
                    ? 'Try adjusting your search query'
                    : 'No organisations registered yet'}
                </p>
              </div>
            )}
          </div>
        </div>

        <BlockConfirmDialog
          open={blockTarget !== null}
          onOpenChange={open => {
            if (!open) setBlockTarget(null);
          }}
          targetName={blockTarget?.name ?? ''}
          targetType="organisation"
          isBlocked={blockTarget?.isBlocked ?? false}
          onConfirm={() => void handleBlockConfirm()}
          isLoading={isBlockLoading}
        />
      </div>
    </TooltipProvider>
  );
}
